import type { ReactNode } from 'react';
import { useCallback, useEffect } from 'react';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { InstantCardSearch } from '@sorare/core/src/components/search/InstantSearch';
import type {
  AnalyticTag,
  Props as InstantCardSearchProps,
} from '@sorare/core/src/components/search/InstantSearch/types';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';
import { useContainerCardsPerPage } from '@sorare/core/src/hooks/search/useContainerCardsPerPage';
import { useVirtualToggle } from '@sorare/core/src/hooks/useVirtualToggle';
import { VIRTUAL_TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';

import { CurrentMarketplaceUserProvider } from '#src/contexts/currentMarketplaceUser/Provider.tsx';
import { useCurrentMarketplaceUserContext } from '#src/contexts/currentMarketplaceUser/index.ts';
import { StartingPredictionValidity } from '#src/searchCards/StartingPredictionValidity/index.tsx';

import { SearchLayout } from './SearchLayout.tsx';
import type { Props } from './types.ts';

interface FullProps extends Props {
  sport: Sport;
  commonMarket?: boolean;
  defaultFilters?: string[];
  getOptionalFilters?: InstantCardSearchProps['getOptionalFilters'];
  analyticsTags: AnalyticTag[];
  distinct?: InstantCardSearchProps['distinct'];
  defaultHitsPerPage?: number;
  editableLists?: boolean;
  alwaysShowFavoriteButton?: boolean;
  searchClient?: InstantCardSearchProps['searchClient'];
  onFavoriteFilterChange?: (v: boolean) => void;
}

const FavoriteFilterChangeNotifier = ({
  onChange,
}: {
  onChange: (v: boolean) => void;
}) => {
  const { currentRefinement } = useVirtualToggle<boolean>({
    name: VIRTUAL_TOGGLE_FILTERS.favoriteFilter.name,
  });

  useEffect(() => {
    if (currentRefinement !== undefined) onChange(currentRefinement);
  }, [currentRefinement, onChange]);

  return null;
};

export const AdvancedCardSearch = ({
  children,
  sport,
  commonMarket,
  analyticsTags,
  defaultFilters,
  getOptionalFilters,
  defaultSort,
  initialIndexUIState,
  cardFilters,
  defaultHitsPerPage,
  editableLists,
  distinct,
  searchClient,
  onFavoriteFilterChange,
  ...rest
}: FullProps) => {
  const index = defaultSort || rest.sorts[0];
  const currentMarketplaceUserContext = useCurrentMarketplaceUserContext();

  const Wrapper = useCallback(
    ({ children: c }: { children: ReactNode }) => (
      <CurrentMarketplaceUserProvider sport={sport}>
        {c}
      </CurrentMarketplaceUserProvider>
    ),
    [sport]
  );

  const [ref, containerHitsPerPage] =
    useContainerCardsPerPage<HTMLDivElement>();

  return (
    <ConditionalWrapper wrap={!currentMarketplaceUserContext} Wrapper={Wrapper}>
      <SearchCardsContextProvider
        commonMarket={Boolean(commonMarket)}
        editableLists={editableLists}
        galleryOwnerSlug={rest.galleryOwnerSlug}
        legacyFilters={!onFavoriteFilterChange}
      >
        <InstantCardSearch
          {...(index && { indexes: [index] })}
          availableSorts={rest.sorts}
          analyticsTags={analyticsTags}
          sport={sport}
          defaultHitsPerPage={containerHitsPerPage}
          defaultFilters={defaultFilters}
          getOptionalFilters={getOptionalFilters}
          urlState
          initialIndexUIState={initialIndexUIState}
          searchClient={searchClient}
          {...(defaultHitsPerPage && { defaultHitsPerPage })}
          {...(distinct && { distinct })}
          {...(rest.attributesToRetrieve && {
            attributesToRetrieve: rest.attributesToRetrieve,
          })}
        >
          {!!onFavoriteFilterChange && (
            <FavoriteFilterChangeNotifier onChange={onFavoriteFilterChange} />
          )}
          <StartingPredictionValidity />
          <SearchLayout
            cardFilters={cardFilters}
            editableLists={editableLists}
            sport={sport}
            {...rest}
          >
            <div ref={ref} />
            {children}
          </SearchLayout>
        </InstantCardSearch>
      </SearchCardsContextProvider>
    </ConditionalWrapper>
  );
};
